import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { coursesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  ArrowLeft, Plus, Video, BookOpen, FileQuestion, Trash2,
  Save, Edit2, Upload, Download, File, GripVertical, Paperclip
} from 'lucide-react';
import { toast } from 'sonner';

const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [lessonDialogOpen, setLessonDialogOpen] = useState(false);
  const [quizDialogOpen, setQuizDialogOpen] = useState(false);
  const [newLesson, setNewLesson] = useState({ title: '', content: '', video_url: '' });
  const [newQuiz, setNewQuiz] = useState({ question: '', options: ['', '', '', ''], correct_answer: 0 });
  const [showQuizAnswers, setShowQuizAnswers] = useState({});
  const fileInputRef = useRef(null);

  const isAdmin = user?.role === 'admin';

  const fetchData = async () => {
    try {
      const [courseRes, attachRes] = await Promise.all([
        coursesApi.getById(id),
        coursesApi.getAttachments(id)
      ]);
      setCourse(courseRes.data);
      setAttachments(attachRes.data);
      setEditData(courseRes.data);
    } catch { toast.error('Failed to load course'); navigate('/courses'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        title: editData.title,
        description: editData.description,
        category: editData.category,
        lessons: editData.lessons,
        quiz: editData.quiz,
        tags: editData.tags
      };
      const res = await coursesApi.update(id, payload);
      setCourse(res.data);
      setEditMode(false);
      toast.success('Course saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this course? This cannot be undone.')) return;
    try {
      await coursesApi.delete(id);
      toast.success('Course deleted');
      navigate('/courses');
    } catch { toast.error('Failed to delete course'); }
  };

  const handleAddLesson = () => {
    if (!newLesson.title.trim()) return;
    const lessons = [...(editData.lessons || []), { ...newLesson, order: (editData.lessons?.length || 0) }];
    setEditData({ ...editData, lessons });
    setNewLesson({ title: '', content: '', video_url: '' });
    setLessonDialogOpen(false);
  };

  const handleRemoveLesson = (index) => {
    const lessons = editData.lessons.filter((_, i) => i !== index);
    setEditData({ ...editData, lessons });
  };

  const handleAddQuizQuestion = () => {
    if (!newQuiz.question.trim()) return;
    const quiz = [...(editData.quiz || []), { ...newQuiz, options: newQuiz.options.filter(o => o.trim()) }];
    setEditData({ ...editData, quiz });
    setNewQuiz({ question: '', options: ['', '', '', ''], correct_answer: 0 });
    setQuizDialogOpen(false);
  };

  const handleRemoveQuiz = (index) => {
    const quiz = editData.quiz.filter((_, i) => i !== index);
    setEditData({ ...editData, quiz });
  };

  const handleUploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await coursesApi.uploadAttachment(id, file);
      toast.success('File uploaded');
      const res = await coursesApi.getAttachments(id);
      setAttachments(res.data);
    } catch { toast.error('Upload failed'); }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const toggleQuizAnswer = (idx) => {
    setShowQuizAnswers(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="w-10 h-10 loading-spinner" /></div>;
  if (!course) return null;

  return (
    <div className="space-y-6" data-testid="course-detail-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/courses')} data-testid="back-to-courses">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          {editMode ? (
            <Input value={editData.title} onChange={e => setEditData({ ...editData, title: e.target.value })} className="text-2xl font-bold" data-testid="edit-course-title" />
          ) : (
            <h1 className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth']">{course.title}</h1>
          )}
          <div className="flex items-center gap-2 mt-1">
            <Badge className={course.category === 'compliance' ? 'bg-purple-100 text-purple-700' : course.category === 'onboarding' ? 'bg-green-100 text-green-700' : course.category === 'certification' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}>
              {course.category}
            </Badge>
            <span className="text-sm text-navy/40">{course.lessons?.length || 0} lessons</span>
            {course.quiz?.length > 0 && <span className="text-sm text-navy/40">{course.quiz.length} quiz questions</span>}
          </div>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            {editMode ? (
              <>
                <Button variant="outline" onClick={() => { setEditMode(false); setEditData(course); }}>Cancel</Button>
                <Button onClick={handleSave} disabled={saving} className="bg-[#40CFDE] text-[#1A2B5E]" data-testid="save-course-btn">
                  <Save className="w-4 h-4 mr-1" />{saving ? 'Saving...' : 'Save'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setEditMode(true)} data-testid="edit-course-btn">
                  <Edit2 className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button variant="outline" onClick={handleDelete} className="text-red-500 hover:bg-red-50" data-testid="delete-course-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Description */}
      {editMode ? (
        <Textarea value={editData.description || ''} onChange={e => setEditData({ ...editData, description: e.target.value })} placeholder="Course description..." rows={3} />
      ) : course.description && (
        <p className="text-navy/70 text-lg leading-relaxed">{course.description}</p>
      )}

      {/* Lessons */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-[#1A2B5E] font-['Amaranth'] flex items-center gap-2">
            <BookOpen className="w-5 h-5" /> Lessons
          </h2>
          {editMode && (
            <Button size="sm" onClick={() => setLessonDialogOpen(true)} className="bg-[#40CFDE] text-[#1A2B5E]" data-testid="add-lesson-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Lesson
            </Button>
          )}
        </div>

        {(editMode ? editData.lessons : course.lessons)?.length === 0 ? (
          <Card className="border-navy/10 border-dashed">
            <CardContent className="py-8 text-center">
              <BookOpen className="w-10 h-10 mx-auto text-navy/20 mb-2" />
              <p className="text-navy/50">No lessons yet</p>
              {isAdmin && <p className="text-sm text-navy/40 mt-1">Click Edit then Add Lesson to start building this course</p>}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="lessons-list">
            {(editMode ? editData.lessons : course.lessons)?.map((lesson, idx) => (
              <Card key={idx} className="border-navy/10" data-testid={`lesson-${idx}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#1A2B5E]/10 flex items-center justify-center text-[#1A2B5E] font-bold text-sm flex-shrink-0">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-[#1A2B5E]">{lesson.title}</h4>
                      {lesson.content && <p className="text-sm text-navy/60 mt-1 whitespace-pre-wrap">{lesson.content}</p>}
                      {lesson.video_url && (
                        <a href={lesson.video_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-2 text-sm text-[#106EFD] hover:underline" data-testid={`lesson-video-${idx}`}>
                          <Video className="w-4 h-4" /> Watch Video
                        </a>
                      )}
                    </div>
                    {editMode && (
                      <button onClick={() => handleRemoveLesson(idx)} className="p-1.5 text-navy/30 hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Quiz */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-[#1A2B5E] font-['Amaranth'] flex items-center gap-2">
            <FileQuestion className="w-5 h-5" /> Quiz / Assessment
          </h2>
          {editMode && (
            <Button size="sm" onClick={() => setQuizDialogOpen(true)} className="bg-[#40CFDE] text-[#1A2B5E]" data-testid="add-quiz-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Question
            </Button>
          )}
        </div>

        {(editMode ? editData.quiz : course.quiz)?.length === 0 ? (
          <Card className="border-navy/10 border-dashed">
            <CardContent className="py-8 text-center">
              <FileQuestion className="w-10 h-10 mx-auto text-navy/20 mb-2" />
              <p className="text-navy/50">No quiz questions yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="quiz-list">
            {(editMode ? editData.quiz : course.quiz)?.map((q, idx) => (
              <Card key={idx} className="border-navy/10" data-testid={`quiz-${idx}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <p className="font-medium text-[#1A2B5E]">Q{idx + 1}: {q.question}</p>
                      <div className="mt-2 space-y-1.5">
                        {q.options?.map((opt, oi) => (
                          <div key={oi} className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${showQuizAnswers[idx] && oi === q.correct_answer ? 'bg-green-100 text-green-700 font-medium' : 'bg-gray-50 text-navy/70'}`}>
                            <span className="w-5 h-5 rounded-full border flex items-center justify-center text-xs font-medium flex-shrink-0">
                              {String.fromCharCode(65 + oi)}
                            </span>
                            <span>{opt}</span>
                          </div>
                        ))}
                      </div>
                      <button onClick={() => toggleQuizAnswer(idx)} className="mt-2 text-xs text-[#106EFD] hover:underline" data-testid={`show-answer-${idx}`}>
                        {showQuizAnswers[idx] ? 'Hide Answer' : 'Show Answer'}
                      </button>
                    </div>
                    {editMode && (
                      <button onClick={() => handleRemoveQuiz(idx)} className="p-1.5 text-navy/30 hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Attachments */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-[#1A2B5E] font-['Amaranth'] flex items-center gap-2">
            <Paperclip className="w-5 h-5" /> Attachments
          </h2>
          {isAdmin && (
            <>
              <input ref={fileInputRef} type="file" onChange={handleUploadAttachment} className="hidden" />
              <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="upload-course-file-btn">
                <Upload className="w-4 h-4 mr-1" /> Upload File
              </Button>
            </>
          )}
        </div>
        {attachments.length === 0 ? (
          <p className="text-sm text-navy/40">No attachments</p>
        ) : (
          <div className="space-y-2">
            {attachments.map(att => (
              <div key={att.id} className="flex items-center gap-3 p-3 rounded-lg border border-navy/10 bg-gray-50/50">
                <File className="w-5 h-5 text-navy/40" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1A2B5E] truncate">{att.filename}</p>
                  <p className="text-[10px] text-navy/40">{(att.file_size / 1024).toFixed(0)} KB</p>
                </div>
                <a href={coursesApi.downloadAttachment(att.id)} target="_blank" rel="noopener noreferrer" className="p-2 text-navy/40 hover:text-[#106EFD]" data-testid={`download-att-${att.id}`}>
                  <Download className="w-4 h-4" />
                </a>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Lesson Dialog */}
      <Dialog open={lessonDialogOpen} onOpenChange={setLessonDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader><DialogTitle className="font-['Amaranth']">Add Lesson</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Lesson Title *</Label>
              <Input value={newLesson.title} onChange={e => setNewLesson({ ...newLesson, title: e.target.value })} placeholder="e.g., Introduction to BSA" data-testid="lesson-title-input" />
            </div>
            <div className="space-y-2">
              <Label>Content / Notes</Label>
              <Textarea value={newLesson.content} onChange={e => setNewLesson({ ...newLesson, content: e.target.value })} placeholder="Lesson content or notes..." rows={4} />
            </div>
            <div className="space-y-2">
              <Label>Video URL (optional)</Label>
              <Input value={newLesson.video_url} onChange={e => setNewLesson({ ...newLesson, video_url: e.target.value })} placeholder="https://youtube.com/watch?v=..." data-testid="lesson-video-input" />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setLessonDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAddLesson} disabled={!newLesson.title.trim()} className="bg-[#40CFDE] text-[#1A2B5E]" data-testid="submit-lesson-btn">Add Lesson</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Quiz Dialog */}
      <Dialog open={quizDialogOpen} onOpenChange={setQuizDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader><DialogTitle className="font-['Amaranth']">Add Quiz Question</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Question *</Label>
              <Textarea value={newQuiz.question} onChange={e => setNewQuiz({ ...newQuiz, question: e.target.value })} placeholder="What is...?" rows={2} data-testid="quiz-question-input" />
            </div>
            <div className="space-y-2">
              <Label>Answer Options</Label>
              {newQuiz.options.map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-navy/5 flex items-center justify-center text-xs font-medium">{String.fromCharCode(65 + i)}</span>
                  <Input value={opt} onChange={e => { const opts = [...newQuiz.options]; opts[i] = e.target.value; setNewQuiz({ ...newQuiz, options: opts }); }} placeholder={`Option ${String.fromCharCode(65 + i)}`} />
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <Label>Correct Answer</Label>
              <div className="flex gap-2">
                {newQuiz.options.map((_, i) => (
                  <button key={i} onClick={() => setNewQuiz({ ...newQuiz, correct_answer: i })} className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${newQuiz.correct_answer === i ? 'bg-green-500 text-white' : 'bg-gray-100 text-navy/50'}`}>
                    {String.fromCharCode(65 + i)}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setQuizDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAddQuizQuestion} disabled={!newQuiz.question.trim()} className="bg-[#40CFDE] text-[#1A2B5E]" data-testid="submit-quiz-btn">Add Question</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CourseDetail;
